<template>
  <div class="container delivery-speed" v-if="$can(['SuperAdmin', 'service', 'manager'])">
    <div class="delivery-head">
      <h4 class="is-size-6-mobile is-size-5-tablet">
        <i class="fal fa-tachometer-alt"></i> {{ $t('deliverySpeed.page_title') }}
      </h4>
      <span v-if="report" class="delivery-note">{{ $t('deliverySpeed.as_of', {time: report.as_of}) }}</span>
      <router-link
          v-if="$can(['SuperAdmin', 'service'])"
          class="delivery-note"
          :to="{name: 'settings.index', query: {section: 'delivery-speed-targets'}}"
      >
        <i class="fal fa-cog"></i> {{ $t('deliverySpeed.settings_title') }}
      </router-link>
    </div>

    <div class="box delivery-filters">
      <div class="buttons has-addons">
        <button class="button is-small seg" :class="{'seg--on': period === 'week'}" @click="setPeriod('week')">{{ $t('deliverySpeed.period_week') }}</button>
        <button class="button is-small seg" :class="{'seg--on': period === 'month'}" @click="setPeriod('month')">{{ $t('deliverySpeed.period_month') }}</button>
      </div>
      <div class="select is-small">
        <select v-model="shipmentType" :aria-label="$t('deliverySpeed.type_label')" @change="fetchData()">
          <option value="">{{ $t('deliverySpeed.type_all') }}</option>
          <option value="express">{{ $t('deliverySpeed.type_express') }}</option>
          <option value="logistics">{{ $t('deliverySpeed.type_logistics') }}</option>
        </select>
      </div>
      <span v-if="lane" class="tag is-medium is-link is-light">
        {{ scopeLabel }}
        <button class="delete is-small" :aria-label="$t('deliverySpeed.clear_filter')" @click="clearScope()"></button>
      </span>
      <span v-if="loading" class="delivery-note">{{ $t('deliverySpeed.loading') }}</span>
    </div>

    <div v-if="unavailable" class="notification is-warning is-light">
      <i class="fal fa-exclamation-triangle"></i> {{ $t('deliverySpeed.unavailable_' + unavailable) }}
      <button class="button is-small" style="margin-left: 8px" @click="fetchData()">{{ $t('deliverySpeed.retry') }}</button>
    </div>

    <div v-if="report" :class="{'delivery-busy': loading}">
      <div v-if="report.stale" class="notification is-warning is-light">
        <i class="fal fa-clock"></i> {{ $t('deliverySpeed.stale_warning', {time: report.as_of}) }}
      </div>

      <!-- What needs attention -->
      <div class="box">
        <h5 class="section-title">{{ $t('deliverySpeed.attention_title') }}</h5>
        <p class="delivery-note">{{ scopeLabel }}</p>
        <p v-if="!report.insights.length" class="delivery-note">{{ $t('deliverySpeed.attention_none') }}</p>
        <ul class="insights">
          <li v-for="(item, index) in report.insights" :key="index" class="insight">
            <span class="insight__badge" :class="'insight__badge--' + item.severity">
              <i class="fal" :class="severityIcon(item.severity)"></i>
              {{ $t('deliverySpeed.severity_' + item.severity) }}
            </span>
            <span class="insight__text">{{ insightText(item) }}</span>
          </li>
        </ul>
      </div>

      <!-- One card per lane: the number is the chart -->
      <h5 class="section-title">
        {{ $t('deliverySpeed.lanes_title', {from: day(report.window.from), to: day(report.window.to)}) }}
      </h5>
      <p class="delivery-note">{{ $t('deliverySpeed.lanes_note') }}</p>
      <div class="columns is-multiline">
        <div v-for="card in targetLanes" :key="card.lane" class="column is-one-fifth-desktop is-half-tablet">
          <div
            class="lane-card"
            :class="{'lane-card--active': lane === card.lane}"
            role="button"
            tabindex="0"
            @click="selectLane(card.lane)"
            @keyup.enter="selectLane(card.lane)"
          >
            <div class="lane-card__name">{{ $t('deliverySpeed.lane_' + card.lane) }}</div>
            <div class="lane-card__target">{{ $t('deliverySpeed.target_days', {days: card.sla_days}) }}</div>
            <div v-if="card.has_province_targets" class="lane-card__target">{{ $t('deliverySpeed.target_some_provinces') }}</div>
            <div class="lane-card__hero">
              <template v-if="card.on_time_pct !== null">{{ card.on_time_pct.toFixed(1) }}<small>%</small></template>
              <template v-else>-</template>
            </div>
            <div class="lane-card__caption">{{ $t('deliverySpeed.on_time_label') }}</div>
            <div v-if="delta(card) !== null" class="lane-card__delta">
              <i class="fal" :class="deltaIcon(card)" :style="{color: deltaColor(card)}"></i>
              {{ deltaText(card) }}
            </div>
            <dl class="lane-card__facts">
              <dt>{{ $t('deliverySpeed.due_bills') }}</dt>
              <dd>{{ fmt(card.due) }}</dd>
              <dt>{{ $t('deliverySpeed.avg_days') }}</dt>
              <dd>{{ card.avg_days === null ? '-' : card.avg_days }}</dd>
              <dt>{{ $t('deliverySpeed.p90_days') }}</dt>
              <dd>{{ card.p90_days === null ? $t('deliverySpeed.p90_unknown') : card.p90_days }}</dd>
              <dt>{{ $t('deliverySpeed.overdue_now') }}</dt>
              <dd><strong>{{ fmt(card.open_overdue) }}</strong></dd>
              <dt>{{ $t('deliverySpeed.over_30_days') }}</dt>
              <dd>{{ fmt(card.open_over_30d) }}</dd>
            </dl>
          </div>
        </div>
      </div>
      <p v-if="otherLanes.length" class="delivery-note">
        <span v-for="other in otherLanes" :key="other.lane" class="other-lane">
          {{ $t('deliverySpeed.no_target_line', {lane: $t('deliverySpeed.lane_' + other.lane), bills: fmt(other.created), days: other.avg_days === null ? '-' : other.avg_days}) }}
        </span>
      </p>

      <!-- After the branch: fast on the network can still mean a week for the customer -->
      <div v-if="report.collection" class="box">
        <h5 class="section-title">{{ $t('deliverySpeed.collection_title') }}</h5>
        <p class="delivery-note">
          {{ scopeLabel }} · {{ $t('deliverySpeed.collection_note', {from: day(report.collection.window.from), to: day(report.collection.window.to)}) }}
        </p>
        <div class="columns">
          <div class="column is-half">
            <h6 class="section-title section-title--small">{{ $t('deliverySpeed.collection_wait_title') }}</h6>
            <SpeedBars :rows="collectionWaitRows" show-share />
          </div>
          <div class="column is-half">
            <h6 class="section-title section-title--small">{{ $t('deliverySpeed.collection_to_sign_title') }}</h6>
            <SpeedBars :rows="collectionToSignRows" show-share />
            <p v-if="report.collection.returned !== null && report.collection.returned !== undefined" class="collection-returned">
              <strong>{{ fmt(report.collection.returned) }}</strong>
              {{ $t('deliverySpeed.collection_returned', {pct: report.collection.returned_pct === null ? '0.0' : report.collection.returned_pct.toFixed(1)}) }}
            </p>
            <p v-else class="delivery-note">{{ $t('deliverySpeed.collection_returned_unknown') }}</p>
          </div>
        </div>
      </div>

      <!-- Trend: two charts, never two axes on one plot -->
      <div class="columns">
        <div class="column is-half">
          <div class="box">
            <h5 class="section-title">{{ $t('deliverySpeed.trend_title') }}</h5>
            <p class="delivery-note">{{ scopeLabel }}</p>
            <div class="chart-box">
              <TrendLineChart :labels="trendLabels" :values="trendPct" :label="$t('deliverySpeed.on_time_label')" color="#2a78d6" unit="%" :line-width="2" :y-max="100" :y-min="trendFloor" />
            </div>
          </div>
        </div>
        <div class="column is-half">
          <div class="box">
            <h5 class="section-title">{{ $t('deliverySpeed.trend_avg_title') }}</h5>
            <p class="delivery-note">{{ scopeLabel }} · {{ $t('deliverySpeed.trend_avg_note', {days: report.avg_settle_days}) }}</p>
            <div class="chart-box">
              <TrendLineChart :labels="trendLabels" :values="trendDays" :label="$t('deliverySpeed.avg_days')" color="#2a78d6" :unit="$t('deliverySpeed.days_unit')" :line-width="2" />
            </div>
          </div>
        </div>
      </div>

      <div class="columns">
        <div class="column is-half">
          <div class="box">
            <h5 class="section-title">{{ $t('deliverySpeed.distribution_title') }}</h5>
            <p class="delivery-note">{{ scopeLabel }}</p>
            <SpeedBars :rows="distributionRows" show-share />
            <p v-if="lastOnTimeBucket >= 0" class="bars-legend">
              <span><i class="swatch swatch--normal"></i>{{ $t('deliverySpeed.legend_within_target') }}</span>
              <span><i class="swatch swatch--serious"></i>{{ $t('deliverySpeed.legend_over_target') }}</span>
              <span><i class="swatch swatch--critical"></i>{{ $t('deliverySpeed.legend_not_arrived') }}</span>
            </p>
          </div>
        </div>
        <div class="column is-half">
          <div class="box">
            <h5 class="section-title">{{ $t('deliverySpeed.why_title') }}</h5>
            <p class="delivery-note">{{ $t('deliverySpeed.why_note', {bills: fmt(report.why_late.late_bills)}) }}</p>
            <p v-if="!whyRows.length" class="delivery-note">{{ $t('deliverySpeed.why_none') }}</p>
            <SpeedBars :rows="whyRows" show-share />
            <div v-if="report.why_late.late_bills" class="why-flags">
              <span class="tag is-light">{{ $t('deliverySpeed.flag_wrong_branch') }}: {{ fmt(report.why_late.flags.wrong_branch) }}</span>
              <span class="tag is-light">{{ $t('deliverySpeed.flag_failed_attempt') }}: {{ fmt(report.why_late.flags.failed_attempt) }}</span>
              <span class="tag is-light">{{ $t('deliverySpeed.flag_dest_change') }}: {{ fmt(report.why_late.flags.dest_change) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Time per trip stage -->
      <div class="box">
        <h5 class="section-title">{{ $t('deliverySpeed.legs_title') }}</h5>
        <p class="delivery-note">{{ scopeLabel }} · {{ $t('deliverySpeed.legs_note') }}</p>
        <div class="table-container">
          <table class="table is-fullwidth is-narrow">
            <thead>
              <tr>
                <th>{{ $t('deliverySpeed.col_leg') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.col_hours') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.col_previous') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.col_bills') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="leg in report.legs" :key="leg.leg">
                <td>{{ $t('deliverySpeed.leg_' + leg.leg) }}</td>
                <td class="has-text-right"><strong>{{ leg.hours === null ? '-' : leg.hours }}</strong></td>
                <td class="has-text-right">{{ leg.hours_prev === null ? '-' : leg.hours_prev }}</td>
                <td class="has-text-right">{{ fmt(leg.bills) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Inside each province, then inside the capital -->
      <div class="box">
        <h5 class="section-title">{{ $t('deliverySpeed.provinces_title') }}</h5>
        <p class="delivery-note">{{ $t('deliverySpeed.provinces_note') }}</p>
        <div class="table-container">
          <table class="table is-fullwidth is-narrow is-hoverable">
            <thead>
              <tr>
                <th>{{ $t('deliverySpeed.col_province') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.col_target_days') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.due_bills') }}</th>
                <th>{{ $t('deliverySpeed.on_time_label') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.avg_days') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.p90_days') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.overdue_now') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.over_7_days') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.over_30_days') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in report.provinces"
                :key="row.province_id"
                class="is-clickable-row"
                :class="{'is-selected-row': isProvinceSelected(row)}"
                @click="selectProvince(row)"
              >
                <td>{{ row.name }}</td>
                <td class="has-text-right">{{ row.sla_days }}</td>
                <td class="has-text-right">{{ fmt(row.due) }}</td>
                <td><Meter :value="row.on_time_pct" /></td>
                <td class="has-text-right">{{ row.avg_days === null ? '-' : row.avg_days }}</td>
                <td class="has-text-right">{{ row.p90_days === null ? (row.due ? $t('deliverySpeed.p90_unknown') : '-') : row.p90_days }}</td>
                <td class="has-text-right"><strong>{{ fmt(row.open_overdue) }}</strong></td>
                <td class="has-text-right">{{ fmt(row.open_over_7d) }}</td>
                <td class="has-text-right">{{ fmt(row.open_over_30d) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h5 class="section-title section-title--sub">{{ $t('deliverySpeed.vte_districts_title') }}</h5>
        <div class="table-container">
          <table class="table is-fullwidth is-narrow">
            <thead>
              <tr>
                <th>{{ $t('deliverySpeed.col_district') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.due_bills') }}</th>
                <th>{{ $t('deliverySpeed.on_time_label') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.avg_days') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.p90_days') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.overdue_now') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.over_7_days') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.over_30_days') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in report.vte_districts" :key="row.district_id">
                <td>{{ row.name }}</td>
                <td class="has-text-right">{{ fmt(row.due) }}</td>
                <td><Meter :value="row.on_time_pct" /></td>
                <td class="has-text-right">{{ row.avg_days === null ? '-' : row.avg_days }}</td>
                <td class="has-text-right">{{ row.p90_days === null ? (row.due ? $t('deliverySpeed.p90_unknown') : '-') : row.p90_days }}</td>
                <td class="has-text-right"><strong>{{ fmt(row.open_overdue) }}</strong></td>
                <td class="has-text-right">{{ fmt(row.open_over_7d) }}</td>
                <td class="has-text-right">{{ fmt(row.open_over_30d) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Overdue right now -->
      <div class="box">
        <h5 class="section-title">{{ $t('deliverySpeed.backlog_title', {bills: fmt(report.backlog.total)}) }}</h5>
        <p class="delivery-note">{{ scopeLabel }} · {{ $t('deliverySpeed.backlog_note', {days: report.backlog.active_days}) }}</p>
        <div class="columns">
          <div class="column is-half">
            <h6 class="section-title section-title--small">{{ $t('deliverySpeed.by_stage') }}</h6>
            <SpeedBars :rows="stageRows" clickable show-share @select="focusStage" />
          </div>
          <div class="column is-half">
            <h6 class="section-title section-title--small">{{ $t('deliverySpeed.aging_title') }}</h6>
            <SpeedBars :rows="agingRows" show-share />
          </div>
        </div>
        <h6 class="section-title section-title--small">{{ $t('deliverySpeed.positions_title') }}</h6>
        <div class="table-container">
          <table class="table is-fullwidth is-narrow is-hoverable">
            <thead>
              <tr>
                <th>{{ $t('deliverySpeed.col_position') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.col_bills') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.col_oldest') }}</th>
                <th class="has-text-right">{{ $t('deliverySpeed.col_idle') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in report.backlog.by_position" :key="row.position_type + row.position_store + row.position_id" class="is-clickable-row" @click="focusPosition(row)">
                <td>{{ $t(row.position_type === 'in_transit' ? 'deliverySpeed.position_heading' : 'deliverySpeed.position_at', {name: row.name}) }}</td>
                <td class="has-text-right"><strong>{{ fmt(row.bills) }}</strong></td>
                <td class="has-text-right">{{ row.oldest_days }}</td>
                <td class="has-text-right">{{ row.idle_days === null ? '-' : row.idle_days }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Who is behind -->
      <div class="box">
        <h5 class="section-title">{{ $t('deliverySpeed.rank_title') }}</h5>
        <p class="delivery-note">{{ scopeLabel }} · {{ $t('deliverySpeed.rank_note', {min: report.min_sample}) }}</p>
        <div class="columns is-multiline">
          <div v-for="table in rankingTables" :key="table.key" class="column is-half-desktop is-full-tablet">
            <h6 class="section-title section-title--small">{{ table.title }}</h6>
            <p v-if="table.note" class="delivery-note">{{ table.note }}</p>
            <p v-if="!table.rows.length" class="delivery-note">{{ $t('deliverySpeed.rank_empty') }}</p>
            <div v-else class="table-container">
              <table class="table is-fullwidth is-narrow">
                <thead>
                  <tr>
                    <th>{{ table.nameTitle }}</th>
                    <th class="has-text-right">{{ $t('deliverySpeed.due_bills') }}</th>
                    <th class="has-text-right">{{ $t('deliverySpeed.on_time_label') }}</th>
                    <th class="has-text-right">{{ table.legTitle }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in table.rows" :key="row.id">
                    <td>{{ row.name }}</td>
                    <td class="has-text-right">{{ fmt(row.due) }}</td>
                    <td class="has-text-right"><strong>{{ row.on_time_pct === null ? '-' : row.on_time_pct.toFixed(1) + '%' }}</strong></td>
                    <td class="has-text-right">{{ row.leg_hours === null ? '-' : row.leg_hours }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Which bills -->
      <div class="box" ref="slowBills">
        <h5 class="section-title">{{ $t('deliverySpeed.slow_title') }}</h5>
        <SlowBills :filters="slowFilters" :focus="focus" @clear-focus="focus = {}" />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import TrendLineChart from '@/components/Chart/TrendLineChart';
import {fetchDeliverySpeedSummary, reasonOf} from '@/services/deliverySpeedService';
import SpeedBars from './components/SpeedBars';
import SlowBills from './components/SlowBills';

// On-time rate as a same-ramp meter next to its number. Province tables stay
// scannable top to bottom; the number stays readable without hovering.
const Meter = {
  functional: true,
  props: {value: {type: Number, default: null}},
  render(h, {props}) {
    if (props.value === null || props.value === undefined) return h('span', '-');
    return h('span', {class: 'meter'}, [
      h('span', {class: 'meter__track'}, [h('span', {class: 'meter__fill', style: {width: props.value + '%'}})]),
      h('span', {class: 'meter__value'}, props.value.toFixed(1) + '%'),
    ]);
  },
};

const VTE_PROVINCE_ID = 10;
const ALIGNED_TARGETS = {3: '3', 4: '4', 5: '5', 7: '6-7'};
const BUCKET_ORDER = ['0-1', '2', '3', '4', '5', '6-7', '8-14', '15-30', '31+'];

export default {
  name: 'DeliverySpeed',
  components: {TrendLineChart, SpeedBars, SlowBills, Meter},
  data() {
    return {
      report: null,
      loading: false,
      unavailable: null,
      period: 'week',
      shipmentType: '',
      lane: '',
      provinceId: null,
      provinceName: '',
      focus: {},
      requestSeq: 0,
    };
  },
  computed: {
    targetLanes() {
      return this.report.lanes.filter((lane) => lane.sla_days > 0);
    },
    otherLanes() {
      return this.report.lanes.filter((lane) => lane.sla_days <= 0);
    },
    scopeLabel() {
      if (!this.lane) return this.$t('deliverySpeed.scope_all');
      const lane = this.$t('deliverySpeed.lane_' + this.lane);
      return this.provinceName ? lane + ': ' + this.provinceName : lane;
    },
    selectedSla() {
      if (!this.lane || !this.report) return 0;
      // bills judged on different targets have no single line between on time and late
      const card = this.report.lanes.find((row) => row.lane === this.lane);
      if (!this.provinceId && card && card.has_province_targets) return 0;
      return this.report.sla_days[this.lane] || 0;
    },
    lastOnTimeBucket() {
      return BUCKET_ORDER.indexOf(ALIGNED_TARGETS[this.selectedSla]);
    },
    trendLabels() {
      return this.report.trend.map((row) => moment(row.date).format('DD/MM'));
    },
    trendPct() {
      return this.report.trend.map((row) => row.on_time_pct);
    },
    trendDays() {
      return this.report.trend.map((row) => row.avg_days);
    },
    // A rate that moves a few points must be readable: the axis starts near the lowest day, not at 0.
    trendFloor() {
      const values = this.trendPct.filter((value) => value !== null);
      if (!values.length) return 0;
      return Math.max(0, Math.floor((Math.min(...values) - 5) / 10) * 10);
    },
    distributionRows() {
      const lastOnTime = this.lastOnTimeBucket;
      return this.report.distribution
        .filter((row) => row.bucket !== 'closed' || row.bills > 0)
        .map((row) => {
          const order = BUCKET_ORDER.indexOf(row.bucket);
          let tone = 'normal';
          if (row.bucket === 'open' || row.bucket === 'closed') tone = 'critical';
          else if (lastOnTime >= 0 && order > lastOnTime) tone = 'serious';
          return {
            key: row.bucket,
            label: order >= 0
              ? this.$t('deliverySpeed.bucket_days', {range: row.bucket})
              : this.$t('deliverySpeed.bucket_' + row.bucket),
            value: row.bills,
            tone,
          };
        });
    },
    collectionWaitRows() {
      return ((this.report.collection || {}).wait || []).map((row) => ({
        key: row.bucket,
        label: this.$t('deliverySpeed.bucket_days', {range: row.bucket}),
        value: row.bills,
        tone: row.bucket === '0-1' || row.bucket === '2-3' ? 'normal' : 'serious',
      }));
    },
    collectionToSignRows() {
      return ((this.report.collection || {}).to_sign || []).map((row) => ({
        key: row.bucket,
        label: this.$t('deliverySpeed.bucket_days', {range: row.bucket}),
        value: row.bills,
        tone: row.bucket === '0-3' ? 'normal' : 'serious',
      }));
    },
    whyRows() {
      return this.report.why_late.by_leg.map((row) => ({
        key: row.leg,
        label: this.$t('deliverySpeed.leg_' + row.leg),
        value: row.bills,
      }));
    },
    stageRows() {
      return this.report.backlog.by_stage.map((row) => ({
        key: row.stage,
        label: this.$t('deliverySpeed.stage_' + row.stage),
        value: row.bills,
      }));
    },
    agingRows() {
      return this.report.backlog.aging.map((row) => ({
        key: row.bucket,
        label: this.$t('deliverySpeed.bucket_days', {range: row.bucket}),
        value: row.bills,
      }));
    },
    rankingTables() {
      const t = (key) => this.$t('deliverySpeed.' + key);
      return [
        {key: 'destinations', title: t('rank_destinations'), nameTitle: t('col_branch'), legTitle: t('col_dc_to_dest_hours'), rows: this.report.rankings.destinations},
        {key: 'origins', title: t('rank_origins'), nameTitle: t('col_branch'), legTitle: t('col_origin_wait_hours'), rows: this.report.rankings.origins},
        {key: 'dcs', title: t('rank_dcs'), note: t('rank_dcs_note'), nameTitle: t('col_dc'), legTitle: t('col_dc_hours'), rows: this.report.rankings.dcs},
      ];
    },
    slowFilters() {
      return {
        period: this.period,
        lane: this.lane || null,
        province_id: this.provinceId,
        shipment_type: this.shipmentType || null,
      };
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      const seq = ++this.requestSeq;
      this.loading = true;
      try {
        const params = {period: this.period};
        if (this.lane) params.lane = this.lane;
        if (this.provinceId) params.province_id = this.provinceId;
        if (this.shipmentType) params.shipment_type = this.shipmentType;
        const report = await fetchDeliverySpeedSummary(params);
        if (seq !== this.requestSeq) return;
        this.report = report;
        this.unavailable = null;
      } catch (e) {
        if (seq !== this.requestSeq) return;
        this.report = null;
        this.unavailable = reasonOf(e);
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    setPeriod(period) {
      if (this.period === period) return;
      this.period = period;
      this.fetchData();
    },
    selectLane(lane) {
      if (this.loading) return;
      this.lane = this.lane === lane && !this.provinceId ? '' : lane;
      this.provinceId = null;
      this.provinceName = '';
      this.focus = {};
      this.fetchData();
    },
    selectProvince(row) {
      if (this.loading) return;
      if (row.province_id === VTE_PROVINCE_ID) {
        this.lane = 'vte_internal';
        this.provinceId = null;
        this.provinceName = '';
      } else {
        this.lane = 'province_internal';
        this.provinceId = row.province_id;
        this.provinceName = row.name;
      }
      this.focus = {};
      this.fetchData();
    },
    isProvinceSelected(row) {
      return row.province_id === VTE_PROVINCE_ID
        ? this.lane === 'vte_internal'
        : this.provinceId === row.province_id;
    },
    clearScope() {
      this.lane = '';
      this.provinceId = null;
      this.provinceName = '';
      this.focus = {};
      this.fetchData();
    },
    // The keyboard still reaches these while new numbers load: a key on an old row must not apply it
    // to the new filter.
    focusStage(row) {
      if (this.loading) return;
      this.focus = {stage: row.key};
      this.scrollToBills();
    },
    focusPosition(row) {
      if (this.loading) return;
      this.focus = {
        position_type: row.position_type,
        position_store: row.position_store,
        position_id: row.position_id,
        position_name: row.name,
      };
      this.scrollToBills();
    },
    scrollToBills() {
      this.$nextTick(() => {
        const box = this.$refs.slowBills;
        if (box && box.scrollIntoView) box.scrollIntoView({behavior: 'smooth', block: 'start'});
      });
    },
    delta(card) {
      if (card.on_time_pct === null || card.on_time_pct_prev === null) return null;
      return Math.round((card.on_time_pct - card.on_time_pct_prev) * 10) / 10;
    },
    deltaIcon(card) {
      const delta = this.delta(card);
      return delta > 0 ? 'fa-arrow-up' : delta < 0 ? 'fa-arrow-down' : 'fa-minus';
    },
    deltaColor(card) {
      const delta = this.delta(card);
      return delta > 0 ? '#0ca30c' : delta < 0 ? '#d03b3b' : '#52514e';
    },
    deltaText(card) {
      const delta = this.delta(card);
      if (delta === 0) return this.$t('deliverySpeed.delta_same');
      return this.$t(delta > 0 ? 'deliverySpeed.delta_up' : 'deliverySpeed.delta_down', {points: Math.abs(delta)});
    },
    severityIcon(severity) {
      return {critical: 'fa-exclamation-circle', warning: 'fa-exclamation-triangle', info: 'fa-info-circle'}[severity];
    },
    insightText(item) {
      const params = {...item.params};
      if (params.lane) params.lane = this.$t('deliverySpeed.lane_' + params.lane);
      if (params.leg) params.leg = this.$t('deliverySpeed.leg_' + params.leg);
      ['bills', 'lane_bills', 'late_bills'].forEach((key) => {
        if (params[key] !== undefined) params[key] = this.fmt(params[key]);
      });
      ['pct', 'previous', 'overall', 'share', 'drop', 'over_week'].forEach((key) => {
        if (params[key] !== undefined) params[key] = Number(params[key]).toFixed(1);
      });
      const key = item.code === 'stuck_at_position'
        ? 'stuck_at_position_' + (item.params.position_type === 'in_transit' ? 'transit' : 'at')
        : item.code;
      return this.$t('deliverySpeed.insight_' + key, params);
    },
    fmt(value) {
      return new Intl.NumberFormat('en-US').format(value || 0);
    },
    day(value) {
      return moment(value).format('DD/MM/YYYY');
    },
  },
};
</script>

<style lang="scss" scoped>
.delivery-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.delivery-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;

  .buttons,
  .buttons .button {
    margin-bottom: 0;
  }

  .select {
    flex: 0 0 auto;
    width: auto;

    select {
      width: auto;
    }
  }
}

// While a new filter loads, the old numbers stay visible but cannot be clicked: a click on them would
// apply an old stage or position to the new filter.
.delivery-busy {
  opacity: 0.55;
  pointer-events: none;
}

.delivery-note {
  font-size: 0.8rem;
  color: #52514e;
  margin-bottom: 8px;
  text-align: left;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #0b0b0b;
  margin-bottom: 4px;
  text-align: left;

  &--sub {
    margin-top: 18px;
  }

  &--small {
    font-size: 0.9rem;
    margin-top: 6px;
  }
}

.insights {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
}

.insight {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 0;
  border-top: 1px solid #e1e0d9;

  &:first-child {
    border-top: 0;
  }
}

.insight__badge {
  flex: 0 0 auto;
  min-width: 88px;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  text-align: center;
  color: #ffffff;

  &--critical {
    background: #d03b3b;
  }

  &--warning {
    background: #fab219;
    color: #0b0b0b;
  }

  &--info {
    background: #52514e;
  }
}

.insight__text {
  font-size: 0.9rem;
  color: #0b0b0b;
}

.seg {
  background: #ffffff;
  color: #52514e;
  border-color: #c3c2b7;

  &--on {
    background: #2a78d6;
    border-color: #2a78d6;
    color: #ffffff;
  }
}

.lane-card {
  height: 100%;
  padding: 12px 14px;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e1e0d9;
  border-radius: 6px;
  cursor: pointer;

  &:hover,
  &:focus {
    border-color: #2a78d6;
    outline: none;
  }

  &--active {
    border: 2px solid #2a78d6;
    padding: 11px 13px;
  }
}

.lane-card__name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0b0b0b;
}

.lane-card__target {
  font-size: 0.75rem;
  color: #52514e;
}

.lane-card__hero {
  font-size: 2.1rem;
  font-weight: 600;
  line-height: 1.1;
  margin-top: 8px;
  color: #0b0b0b;

  small {
    font-size: 1.1rem;
    font-weight: 400;
  }
}

.lane-card__caption {
  font-size: 0.75rem;
  color: #52514e;
}

.lane-card__delta {
  font-size: 0.78rem;
  color: #52514e;
  margin-top: 4px;
}

.lane-card__facts {
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 8px;
  row-gap: 2px;
  margin: 8px 0 0;
  padding-top: 8px;
  border-top: 1px solid #e1e0d9;
  font-size: 0.78rem;

  dt {
    color: #52514e;
    text-align: left;
  }

  dd {
    margin: 0;
    color: #0b0b0b;
    text-align: right;
  }
}

.other-lane {
  margin-right: 16px;
}

.chart-box {
  position: relative;
  height: 260px;

  > div {
    height: 100%;
  }
}

.bars-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 8px;
  font-size: 0.78rem;
  color: #52514e;
}

.swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 5px;

  &--normal {
    background: #2a78d6;
  }

  &--serious {
    background: #ec835a;
  }

  &--critical {
    background: #d03b3b;
  }
}

.collection-returned {
  margin-top: 10px;
  font-size: 0.85rem;
  color: #52514e;
  text-align: left;

  strong {
    color: #0b0b0b;
  }
}

.why-flags {
  margin-top: 10px;

  .tag {
    margin: 0 6px 4px 0;
  }
}

.table-container {
  width: 100%;
  overflow-x: auto;
}

.table {
  th,
  td {
    white-space: nowrap;
    font-size: 0.85rem;
  }

  thead th {
    background: #f5f5f3;
    font-weight: 600;
  }
}

.is-clickable-row {
  cursor: pointer;
}

.is-selected-row {
  background: #eaf2fc;
}

// The functional Meter renders outside this file's scope id.
.delivery-speed ::v-deep .meter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 130px;
}

.delivery-speed ::v-deep .meter__track {
  flex: 1 1 auto;
  height: 8px;
  background: #e8e7e1;
  border-radius: 4px;
  overflow: hidden;
}

.delivery-speed ::v-deep .meter__fill {
  display: block;
  height: 100%;
  background: #2a78d6;
}

.delivery-speed ::v-deep .meter__value {
  font-weight: 600;
  min-width: 46px;
  text-align: right;
}
</style>
